import React, { useState } from 'react';
import { TruckIcon, ClockIcon, CheckCircleIcon, XCircleIcon, PlusIcon, MapPinIcon, AlertCircleIcon, CalendarIcon, UserIcon } from 'lucide-react';
interface ForemanRequest {
  id: string;
  foremanName: string;
  jobName: string;
  jobNumber: string;
  currentLocation: string;
  requestType: 'trucks' | 'tonnage' | 'both';
  trucksRequested?: number;
  tonnageRequested?: number;
  truckType: string;
  material: string;
  urgency: 'low' | 'medium' | 'high';
  requestedDate: string;
  requestTime: string;
  reason: string;
  status: 'pending' | 'approved' | 'declined';
  estimatedHours?: number;
}
interface ForemanRequestsManagerProps {
  onCreateJobFromRequest: (request: ForemanRequest) => void;
}
export const ForemanRequestsManager = ({
  onCreateJobFromRequest
}: ForemanRequestsManagerProps) => {
  const [requests, setRequests] = useState<ForemanRequest[]>([{
    id: 'req-1',
    foremanName: 'John Smith',
    jobName: 'Downtown Commercial Complex Phase 1',
    jobNumber: 'DCC-2024-001',
    currentLocation: 'Downtown SLC Project',
    requestType: 'both',
    trucksRequested: 3,
    tonnageRequested: 75,
    truckType: 'dump truck 12',
    material: 'Road Base',
    urgency: 'high',
    requestedDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    requestTime: '07:00',
    reason: 'Running low on road base material. Need additional supply to maintain project schedule.',
    status: 'pending',
    estimatedHours: 4
  }, {
    id: 'req-2',
    foremanName: 'Mike Johnson',
    jobName: 'Highway 89 Expansion Project',
    jobNumber: 'HWY-2024-002',
    currentLocation: 'North Valley Project',
    requestType: 'trucks',
    trucksRequested: 2,
    truckType: 'strong arm 14',
    material: 'Gravel',
    urgency: 'medium',
    requestedDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    requestTime: '08:30',
    reason: 'Additional trucks needed for increased production schedule.',
    status: 'pending',
    estimatedHours: 6
  }, {
    id: 'req-3',
    foremanName: 'Sarah Williams',
    jobName: 'Residential Development Phase 2',
    jobNumber: 'RES-2024-003',
    currentLocation: 'Sugar House Development',
    requestType: 'tonnage',
    tonnageRequested: 50,
    truckType: 'belly dump',
    material: 'Sand',
    urgency: 'low',
    requestedDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    requestTime: '06:00',
    reason: 'Preparing for next phase of construction. Early delivery preferred.',
    status: 'pending',
    estimatedHours: 3
  }]);
  const [selectedRequests, setSelectedRequests] = useState<string[]>([]);
  const handleApproveRequest = (requestId: string) => {
    setRequests(requests.map(req => req.id === requestId ? {
      ...req,
      status: 'approved' as const
    } : req));
  };
  const handleDeclineRequest = (requestId: string) => {
    setRequests(requests.map(req => req.id === requestId ? {
      ...req,
      status: 'declined' as const
    } : req));
  };
  const handleBulkApprove = () => {
    setRequests(requests.map(req => selectedRequests.includes(req.id) ? {
      ...req,
      status: 'approved' as const
    } : req));
    setSelectedRequests([]);
  };
  const handleBulkDecline = () => {
    setRequests(requests.map(req => selectedRequests.includes(req.id) ? {
      ...req,
      status: 'declined' as const
    } : req));
    setSelectedRequests([]);
  };
  const handleCreateJob = (request: ForemanRequest) => {
    onCreateJobFromRequest(request);
  };
  const handleSelectRequest = (requestId: string) => {
    setSelectedRequests(prev => prev.includes(requestId) ? prev.filter(id => id !== requestId) : [...prev, requestId]);
  };
  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'high':
        return 'text-red-600 bg-red-100';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100';
      case 'low':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'text-green-600 bg-green-100';
      case 'declined':
        return 'text-red-600 bg-red-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };
  const pendingRequests = requests.filter(req => req.status === 'pending');
  const approvedRequests = requests.filter(req => req.status === 'approved');
  return <div className="space-y-6" data-id="element-1737">
      {/* Header with bulk actions */}
      <div className="flex justify-between items-center" data-id="element-1738">
        <div data-id="element-1739">
          <h2 className="text-xl font-bold" data-id="element-1740">Foreman Requests</h2>
          <p className="text-gray-600" data-id="element-1741">
            Review and approve material and truck requests from your foremen
          </p>
        </div>
        {selectedRequests.length > 0 && <div className="flex gap-2" data-id="element-1742">
            <button onClick={handleBulkApprove} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center gap-2" data-id="element-1743">
              <CheckCircleIcon size={16} data-id="element-1744" />
              Approve Selected ({selectedRequests.length})
            </button>
            <button onClick={handleBulkDecline} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md flex items-center gap-2" data-id="element-1745">
              <XCircleIcon size={16} data-id="element-1746" />
              Decline Selected ({selectedRequests.length})
            </button>
          </div>}
      </div>
      {/* Pending Requests */}
      <div className="bg-white rounded-lg shadow-md" data-id="element-1747">
        <div className="p-4 border-b bg-yellow-50" data-id="element-1748">
          <div className="flex items-center gap-2" data-id="element-1749">
            <AlertCircleIcon size={20} className="text-yellow-600" data-id="element-1750" />
            <h3 className="font-medium" data-id="element-1751">
              Pending Requests ({pendingRequests.length})
            </h3>
          </div>
        </div>
        {pendingRequests.length === 0 ? <div className="p-8 text-center text-gray-500" data-id="element-1752">
            <TruckIcon size={40} className="mx-auto mb-2 text-gray-400" data-id="element-1753" />
            <p data-id="element-1754">No pending requests from foremen</p>
          </div> : <div className="divide-y" data-id="element-1755">
            {pendingRequests.map(request => <div key={request.id} className="p-4 hover:bg-gray-50" data-id="element-1756">
                <div className="flex items-start gap-4" data-id="element-1757">
                  <input type="checkbox" checked={selectedRequests.includes(request.id)} onChange={() => handleSelectRequest(request.id)} className="mt-1" data-id="element-1758" />
                  <div className="flex-1" data-id="element-1759">
                    <div className="flex items-center justify-between mb-2" data-id="element-1760">
                      <div className="flex items-center gap-3" data-id="element-1761">
                        <h4 className="font-medium" data-id="element-1762">{request.jobName}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getUrgencyColor(request.urgency)}`} data-id="element-1763">
                          {request.urgency.toUpperCase()} PRIORITY
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-1764">
                        <CalendarIcon size={16} data-id="element-1765" />
                        {request.requestedDate} at {request.requestTime}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-3" data-id="element-1766">
                      <div className="flex items-center gap-2" data-id="element-1767">
                        <UserIcon size={16} className="text-gray-400" data-id="element-1768" />
                        <div data-id="element-1769">
                          <div className="text-sm font-medium" data-id="element-1770">
                            {request.foremanName}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-1771">
                            {request.jobNumber}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-1772">
                        <MapPinIcon size={16} className="text-gray-400" data-id="element-1773" />
                        <div data-id="element-1774">
                          <div className="text-sm font-medium" data-id="element-1775">
                            {request.currentLocation}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-1776">
                            Current job site
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-1777">
                        <TruckIcon size={16} className="text-gray-400" data-id="element-1778" />
                        <div data-id="element-1779">
                          <div className="text-sm font-medium" data-id="element-1780">
                            {request.material}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-1781">
                            {request.truckType}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-1782">
                        <ClockIcon size={16} className="text-gray-400" data-id="element-1783" />
                        <div data-id="element-1784">
                          <div className="text-sm font-medium" data-id="element-1785">
                            {request.requestType === 'trucks' && `${request.trucksRequested} trucks`}
                            {request.requestType === 'tonnage' && `${request.tonnageRequested} tons`}
                            {request.requestType === 'both' && `${request.trucksRequested} trucks, ${request.tonnageRequested} tons`}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-1786">
                            Est. {request.estimatedHours}hrs
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mb-3" data-id="element-1787">
                      <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded-md" data-id="element-1788">
                        <strong data-id="element-1789">Reason:</strong> {request.reason}
                      </div>
                    </div>
                    <div className="flex justify-end gap-2" data-id="element-1790">
                      <button onClick={() => handleDeclineRequest(request.id)} className="px-3 py-1 border border-red-300 text-red-600 rounded-md hover:bg-red-50 flex items-center gap-1" data-id="element-1791">
                        <XCircleIcon size={16} data-id="element-1792" />
                        Decline
                      </button>
                      <button onClick={() => handleApproveRequest(request.id)} className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-1" data-id="element-1793">
                        <CheckCircleIcon size={16} data-id="element-1794" />
                        Approve
                      </button>
                    </div>
                  </div>
                </div>
              </div>)}
          </div>}
      </div>
      {/* Approved Requests */}
      {approvedRequests.length > 0 && <div className="bg-white rounded-lg shadow-md" data-id="element-1795">
          <div className="p-4 border-b bg-green-50" data-id="element-1796">
            <div className="flex items-center justify-between" data-id="element-1797">
              <div className="flex items-center gap-2" data-id="element-1798">
                <CheckCircleIcon size={20} className="text-green-600" data-id="element-1799" />
                <h3 className="font-medium" data-id="element-1800">
                  Approved Requests ({approvedRequests.length})
                </h3>
              </div>
              <p className="text-sm text-green-700" data-id="element-1801">Ready to convert to jobs</p>
            </div>
          </div>
          <div className="divide-y" data-id="element-1802">
            {approvedRequests.map(request => <div key={request.id} className="p-4 hover:bg-gray-50" data-id="element-1803">
                <div className="flex items-center justify-between" data-id="element-1804">
                  <div className="flex items-center gap-4" data-id="element-1805">
                    <div className="flex-1" data-id="element-1806">
                      <div className="flex items-center gap-3 mb-1" data-id="element-1807">
                        <h4 className="font-medium" data-id="element-1808">{request.jobName}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`} data-id="element-1809">
                          APPROVED
                        </span>
                      </div>
                      <div className="text-sm text-gray-600" data-id="element-1810">
                        {request.foremanName} • {request.requestedDate} at{' '}
                        {request.requestTime} •
                        {request.requestType === 'trucks' && ` ${request.trucksRequested} trucks`}
                        {request.requestType === 'tonnage' && ` ${request.tonnageRequested} tons`}
                        {request.requestType === 'both' && ` ${request.trucksRequested} trucks, ${request.tonnageRequested} tons`}
                        {` of ${request.material}`}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => handleCreateJob(request)} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2" data-id="element-1811">
                    <PlusIcon size={16} data-id="element-1812" />
                    Create Job
                  </button>
                </div>
              </div>)}
          </div>
        </div>}
    </div>;
};